import { Chip } from '@mui/material'
import React from 'react'
import { black, grey, shade } from '../../../colors'
import { getBarChartColors, getContrastTextColor } from './Charts/BarChart/helper'
import { useReportsContext } from '../ReportsContext'
import CustomTooltip from '../../../components/CustomTooltip'

interface ReportsChipProps {
  index: number
  label: string | number | JSX.Element
  icon?: JSX.Element
  tooltipTitle?: React.ReactNode
  ariaLabel?: string
  maxWidth?: number | string
  backgroundColor?: string
  textColor?: string
  onDelete?: () => void
}

const ReportsChip: React.FC<ReportsChipProps> = ({
  index,
  label,
  icon,
  tooltipTitle,
  ariaLabel,
  maxWidth,
  backgroundColor,
  textColor,
  onDelete
}): JSX.Element => {
  const { selectedTrend } = useReportsContext()
  const barColors = getBarChartColors(selectedTrend)

  const getChipTextColor = (index: number, bgColorOverride?: string): string => {
    const bgColor = bgColorOverride ?? barColors[index] ?? shade[650]
    return getContrastTextColor(bgColor, grey[0], black[100])
  }

  const chip = (
    <Chip
      key={index}
      size="small"
      icon={icon}
      label={label}
      aria-label={ariaLabel}
      onDelete={onDelete}
      sx={{
        bgcolor: backgroundColor ?? barColors[index] ?? shade[650],
        color: textColor ?? getChipTextColor(index, backgroundColor),
        fontSize: '14px',
        lineHeight: 4,
        fontWeight: 500,
        borderRadius: 2.5,
        maxWidth,
        '& .MuiChip-label': {
          px: 2,
          py: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          maxWidth: maxWidth
            ? typeof maxWidth === 'number'
              ? `${maxWidth - 32}px`
              : `calc(${maxWidth} - 32px)`
            : 'none'
        },
        '& .MuiChip-icon': {
          color: textColor ?? getChipTextColor(index, backgroundColor),
          opacity: 1
        },
        '& .MuiChip-deleteIcon': {
          color: textColor ?? getChipTextColor(index, backgroundColor),
          '&:hover': {
            color: textColor ?? getChipTextColor(index, backgroundColor)
          }
        }
      }}
    />
  )

  if (!tooltipTitle) {
    return chip
  }

  return (
    <CustomTooltip title={tooltipTitle}>
      <span>{chip}</span>
    </CustomTooltip>
  )
}

export default ReportsChip
